import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'

import { QueryError } from './QueryError'

interface Props {
  isPending: boolean
  isError: boolean
  error: unknown
  isEmpty?: boolean
  emptyMessage?: string
  onRetry?: () => void
  children: ReactNode
}

/**
 * Loading, error and empty in one place.
 *
 * Written once because the three are easy to get subtly wrong per screen — an
 * empty list rendered as a blank panel reads as a broken page, and a failed
 * request rendered as an empty list reads as "you have nothing", which is
 * worse than an error.
 */
export function DataState({
  isPending,
  isError,
  error,
  isEmpty = false,
  emptyMessage = 'هنوز موردی برای نمایش نیست.',
  onRetry,
  children,
}: Props) {
  if (isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress aria-label="در حال بارگذاری" />
      </Box>
    )
  }
  if (isError) {
    return <QueryError error={error} {...(onRetry ? { onRetry } : {})} />
  }
  if (isEmpty) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    )
  }
  return <>{children}</>
}
